import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Snackbar,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import EmailIcon from '@material-ui/icons/Email';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMailOrbitEnabled } from 'src/utils/mailorbitFeature';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getMailOrbitErrorKey,
} from 'src/utils/MailOrbitService';
import { useOpenConversation } from './openConversation';
import { setUnreadCount } from './unreadStore';

const POLL_MS = 30000;
const TAKE = 20;

// eslint-disable-next-line react/prop-types
const NotificationBellInner = ({ className }) => {
  const { t } = useTranslation(['mailorbitWork', 'mailorbit']);
  const history = useHistory();
  const { openConversation, conversationDialog } = useOpenConversation(history);
  const anchorRef = useRef(null);
  const seenIds = useRef(null); // null: ilk yukleme (toast gosterme)
  const [data, setData] = useState({ items: [], unreadCount: 0 });
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const res = await getNotifications({ take: TAKE });
      const items = (res && res.items) || [];
      if (seenIds.current !== null) {
        const fresh = items.filter(
          n => !n.isRead && n.type === 'Assigned' && !seenIds.current.has(n.id)
        );
        if (fresh.length > 0) setToast(true);
      }
      seenIds.current = new Set(items.map(n => n.id));
      const unread = (res && res.unreadCount) || 0;
      setData({ items, unreadCount: unread });
      setUnreadCount(unread);
      setError(null);
    } catch (e) {
      // Periyodik sorguda hata toast ile spam edilmez; zil acilinca gosterilir.
      setError(getMailOrbitErrorKey(e));
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const handleClick = async n => {
    setOpen(false);
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
      } catch (e) {
        // okundu isaretlenemese de ekran acilir
      }
      fetchNotifications();
    }
    openConversation({ conversationId: n.conversationId });
  };

  const handleReadAll = async () => {
    try {
      await markAllNotificationsRead();
    } catch (e) {
      setError(getMailOrbitErrorKey(e));
    }
    fetchNotifications();
  };

  const label = n =>
    n.type === 'Assigned' ? t('notif_assigned') : t('notif_generic', { type: n.type });

  return (
    <>
      <IconButton
        ref={anchorRef}
        className={className}
        onClick={() => setOpen(true)}
        title={t('bell_title')}
      >
        <Badge badgeContent={data.unreadCount} color="error">
          <EmailIcon style={{ color: 'white' }} />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <div style={{ width: 340, maxWidth: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
            }}
          >
            <Typography variant="subtitle1">{t('bell_title')}</Typography>
            <Button size="small" onClick={handleReadAll} disabled={!data.unreadCount}>
              {t('notif_read_all')}
            </Button>
          </div>
          <Divider />
          {error ? <Alert severity="error">{t(`mailorbit:${error}`)}</Alert> : null}
          {data.items.length === 0 && !error ? (
            <Typography variant="body2" style={{ padding: 16 }}>
              {t('notif_empty')}
            </Typography>
          ) : (
            <List dense>
              {data.items.map(n => (
                <ListItem
                  key={n.id}
                  button
                  onClick={() => handleClick(n)}
                  style={n.isRead ? undefined : { backgroundColor: 'rgba(0,0,0,0.04)' }}
                >
                  <ListItemText
                    primary={label(n)}
                    secondary={moment(n.createdAtUtc).format('DD.MM.YYYY HH:mm')}
                    primaryTypographyProps={{ style: { fontWeight: n.isRead ? 400 : 600 } }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </div>
      </Popover>
      <Snackbar
        open={toast}
        autoHideDuration={6000}
        onClose={() => setToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="info" onClose={() => setToast(false)}>
          {t('notif_assigned')}
        </Alert>
      </Snackbar>
      {conversationDialog}
    </>
  );
};

/**
 * MailOrbit bildirim zili. Yalnizca gate acik tenant'larda render edilir; kapaliysa null
 * doner ve hicbir istek yapilmaz (Email2Case tenant'lari etkilenmez).
 */
// eslint-disable-next-line react/prop-types
const NotificationBell = ({ className }) => {
  const enabled = useMailOrbitEnabled();
  if (!enabled) return null;
  return <NotificationBellInner className={className} />;
};

export default NotificationBell;
